import axios from 'axios';
import AppConst from '../apis/appconst';
import { toast } from 'react-toastify';

class AutoImportService {
  constructor() {
    this.watchFolder = null;
    this.isWatching = false;
    this.checkInterval = null;
    this.lastProcessedFiles = new Set();
  }

  startWatching(folderPath, onImport) {
    if (this.isWatching) {
      console.log('Already watching folder');
      return;
    }

    this.watchFolder = folderPath;
    this.isWatching = true;

    this.checkInterval = setInterval(() => {
      this.checkForNewFiles(onImport);
    }, 5000); // Check every 5 seconds

    toast.info('🔍 Auto-import service started');
  }

  stopWatching() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
    this.isWatching = false;
    this.lastProcessedFiles.clear();
    toast.info('Auto-import service stopped');
  }

  async checkForNewFiles(onImport) {
    try {
      const response = await axios.get(
        `${AppConst.BASE_URL}${AppConst.scanImportFolderUrl}`,
        { params: { folder: this.watchFolder } }
      );

      if (response.data.status && response.data.files && response.data.files.length > 0) {
        for (const fileInfo of response.data.files) {
          if (!this.lastProcessedFiles.has(fileInfo.filename)) {
            console.log('📄 New file detected:', fileInfo.filename);
            await this.processFile(fileInfo, onImport);
            this.lastProcessedFiles.add(fileInfo.filename);
          }
        }
      }
    } catch (error) {
      console.error('Error checking files:', error);
    }
  }

  async processFile(fileInfo, onImport) {
    try {
      const parsed = this.parseFileContent(fileInfo.content, fileInfo.extension);
      
      if (!parsed || parsed.length === 0) {
        console.warn('No data in file:', fileInfo.filename);
        return;
      }

      const patientId = this.extractPatientId(parsed);
      
      if (!patientId) {
        toast.warning(`❌ No patient ID in ${fileInfo.filename}`);
        return;
      }

      // Call backend to auto-import
      const importResponse = await axios.post(
        `${AppConst.BASE_URL}${AppConst.autoImportResultsUrl}`,
        {
          patient_id: patientId,
          results: JSON.stringify(parsed)
        }
      );

      if (importResponse.data.status) {
        toast.success(`✅ ${importResponse.data.message}`);
        if (onImport) {
          await onImport(patientId, parsed, fileInfo.filename, importResponse.data);
        }
      } else {
        toast.error(importResponse.data.message || 'Import failed');
      }

    } catch (error) {
      console.error('Process file error:', error);
      toast.error(`Failed to process ${fileInfo.filename}`);
    }
  }

  parseFileContent(content, extension) {
    try {
      if (extension === 'csv' || extension === 'txt') {
        return this.parseCSV(content);
      }
      return null;
    } catch (error) {
      console.error('Parse error:', error);
      return null;
    }
  }

  parseCSV(content) {
    const lines = content.split('\n').filter(line => line.trim());
    if (lines.length === 0) return null;

    const delimiter = this.detectDelimiter(lines[0]);
    const headers = lines[0].split(delimiter).map(h => h.trim());
    const results = [];

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(delimiter).map(v => v.trim());
      const row = {};
      
      headers.forEach((header, index) => {
        row[header] = values[index] || '';
      });
      
      if (Object.values(row).some(v => v)) {
        results.push(row);
      }
    }

    return results;
  }

  detectDelimiter(line) {
    const delimiters = [',', ';', '\t', '|'];
    let maxCount = 0;
    let detected = ',';
    
    delimiters.forEach(delimiter => {
      const count = (line.match(new RegExp('\\' + delimiter, 'g')) || []).length;
      if (count > maxCount) {
        maxCount = count;
        detected = delimiter;
      }
    });
    
    return detected;
  }

  extractPatientId(parsedData) {
    const patientIdColumns = [
      'PatientID', 'Patient_ID', 'patient_id',
      'UHID', 'uhid', 'Uhid',
      'SampleID', 'Sample_ID', 'sample_id'
    ];

    const firstRow = parsedData[0];
    
    for (const col of patientIdColumns) {
      if (firstRow[col] && String(firstRow[col]).trim()) {
        return String(firstRow[col]).trim();
      }
    }

    return null;
  }
}

export default new AutoImportService();